// DocumentSetFilter.tsx (T100)
// Admin-only filter that allows passing an explicit ownerId to document queries.
// Only rendered when the authenticated user has the Admin role.
import React, { useState } from 'react';
import { Input, Button, Space, Typography } from 'antd';
import { SearchOutlined, ClearOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchDocumentsBySet, setOwnerId } from '../../redux/slices/documentSetSlice';

const { Text } = Typography;

/**
 * Decode a JWT (without signature verification) and extract the roles array.
 * Returns [] if the token is missing or invalid.
 */
function getRolesFromToken(): string[] {
    try {
        const token = localStorage.getItem('access_token');
        if (!token) return [];
        const payload = JSON.parse(atob(token.split('.')[1]));
        const roleKey =
            'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
        const raw = payload[roleKey] ?? payload['role'] ?? [];
        const roles: string[] = Array.isArray(raw) ? raw : [raw];
        return roles.map((r) => r.toLowerCase());
    } catch {
        return [];
    }
}

export function useIsAdmin(): boolean {
    return getRolesFromToken().includes('admin');
}

const DocumentSetFilter: React.FC = () => {
    const dispatch = useAppDispatch();
    const currentSet = useAppSelector((s) => s.documentSet.currentSet);
    const isAdmin = useIsAdmin();
    const [inputValue, setInputValue] = useState('');

    if (!isAdmin) return null;

    const handleApply = () => {
        const trimmed = inputValue.trim() || null;
        dispatch(setOwnerId(trimmed));
        dispatch(fetchDocumentsBySet({ setType: currentSet, ownerId: trimmed ?? undefined }));
    };

    const handleClear = () => {
        setInputValue('');
        dispatch(setOwnerId(null));
        dispatch(fetchDocumentsBySet({ setType: currentSet }));
    };

    return (
        <div style={{ marginTop: 8, marginBottom: 12 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
                Фильтр по владельцу (Admin)
            </Text>
            <Space.Compact>
                <Input
                    placeholder="Owner GUID"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onPressEnter={handleApply}
                    style={{ width: 280 }}
                    allowClear
                />
                <Button icon={<SearchOutlined />} onClick={handleApply}>
                    Найти
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleClear}>
                    Сбросить
                </Button>
            </Space.Compact>
        </div>
    );
};

export default DocumentSetFilter;
